"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var react_1 = __importStar(require("react"));
var StatsigContext_1 = __importDefault(require("./StatsigContext"));
var statsig_js_1 = __importDefault(require("statsig-js"));
var Statsig_1 = __importDefault(require("./Statsig"));
var SDKVersion_1 = require("./SDKVersion");
function usePrevious(value) {
    var ref = (0, react_1.useRef)(null);
    (0, react_1.useEffect)(function () {
        ref.current = value;
    }, [value]);
    return ref.current;
}
/**
 * The StatsigProvider is the top level component from which all React SDK components derive
 * It initializes the SDK so child components can use FeatureGate and DynamicConfig values
 *
 * The provider accepts the same SDK initialization parameters as the statsig-js SDK.
 *
 * We recommend you place this at the entry point of your app and pass waitForInitialization = true
 * to ensure the SDK is initialized and all values are up to date prior to rendering anything.
 * @param props
 * @returns
 */
function StatsigProvider(_a) {
    var children = _a.children, sdkKey = _a.sdkKey, user = _a.user, setUser = _a.setUser, options = _a.options, waitForCache = _a.waitForCache, waitForInitialization = _a.waitForInitialization, initializingComponent = _a.initializingComponent, mountKey = _a.mountKey, _b = _a.shutdownOnUnmount, shutdownOnUnmount = _b === void 0 ? false : _b, rnDeps = _a._reactNativeDependencies;
    var isReactNative = !!rnDeps;
    var _c = (0, react_1.useState)(false), hasCacheValues = _c[0], setHasCacheValues = _c[1];
    var _d = (0, react_1.useState)(false), hasNetworkValues = _d[0], setHasNetworkValues = _d[1];
    var resolver = (0, react_1.useRef)(null);
    var _e = (0, react_1.useState)(0), userVersion = _e[0], setUserVersion = _e[1];
    var statsigPromise = (0, react_1.useRef)(new Promise(function (resolve) {
        resolver.current = resolve;
    }));
    var userMemo = (0, react_1.useMemo)(function () {
        return user;
    }, [JSON.stringify(user)]);
    var prevMountKey = usePrevious(mountKey !== null && mountKey !== void 0 ? mountKey : null);
    (0, react_1.useEffect)(function () {
        if (Statsig_1.default.initializeCalled()) {
            statsigPromise.current = new Promise(function (resolve) {
                resolver.current = resolve;
            });
            var unmount_1 = mountKey === undefined || prevMountKey !== mountKey;
            if (unmount_1) {
                setHasNetworkValues(false);
                setHasCacheValues(false);
            }
            Statsig_1.default.updateUser(user).then(function () {
                resolver.current && resolver.current();
                setUserVersion(function (version) { return version + 1; });
                if (unmount_1) {
                    setHasNetworkValues(true);
                    setHasCacheValues(true);
                }
            });
            return;
        }
        Statsig_1.default.setSDKPackageInfo({
            sdkType: 'react-client',
            sdkVersion: SDKVersion_1.version,
        });
        if (isReactNative) {
            Statsig_1.default.setSDKPackageInfo(rnDeps.SDKPackageInfo);
            Statsig_1.default.setAppState(rnDeps.AppState);
            Statsig_1.default.setAsyncStorage(rnDeps.AsyncStorage);
            Statsig_1.default.setNativeModules(rnDeps.NativeModules);
            Statsig_1.default.setPlatform(rnDeps.Platform);
            Statsig_1.default.setRNDeviceInfo(rnDeps.RNDevice);
            Statsig_1.default.setReactNativeUUID(rnDeps.ReactNativeUUID);
            // expo
            Statsig_1.default.setExpoConstants(rnDeps.Constants);
            Statsig_1.default.setExpoDevice(rnDeps.ExpoDevice);
        }
        Statsig_1.default.setOnCacheLoadedCallback(function () {
            setHasCacheValues(true);
        });
        Statsig_1.default.initialize(sdkKey, userMemo, options).then(function () {
            setHasNetworkValues(true);
            resolver.current && resolver.current();
        });
        if (typeof window !== 'undefined') {
            window.__STATSIG_SDK__ = Statsig_1.default;
            window.__STATSIG_JS_SDK__ = statsig_js_1.default;
            window.__STATSIG_RERENDER_OVERRIDE__ = function () {
                setUserVersion(userVersion + 1);
            };
        }
    }, [userMemo]);
    (0, react_1.useEffect)(function () {
        Statsig_1.default.setReactContextUpdater(function () {
            return setUserVersion(function (version) { return version + 1; });
        });
        return function () {
            if (shutdownOnUnmount) {
                Statsig_1.default.shutdown();
            }
            Statsig_1.default.setReactContextUpdater(null);
        };
    }, []);
    var child = pickChildToRender(waitForCache === true, waitForInitialization === true, hasNetworkValues, hasCacheValues, children, initializingComponent);
    var contextValue = (0, react_1.useMemo)(function () { return ({
        initialized: hasNetworkValues,
        statsigPromise: statsigPromise,
        userVersion: userVersion,
        initStarted: Statsig_1.default.initializeCalled(),
        updateUser: setUser !== null && setUser !== void 0 ? setUser : (function () {
            // noop
        }),
    }); }, [
        hasNetworkValues,
        statsigPromise,
        userVersion,
        Statsig_1.default.initializeCalled(),
        setUser,
    ]);
    return (react_1.default.createElement(StatsigContext_1.default.Provider, { value: contextValue }, child));
}
exports.default = StatsigProvider;
function pickChildToRender(waitForCache, waitForInitialization, hasNetworkValues, hasCacheValues, children, initializingComponent) {
    // No Need to Wait
    if (hasNetworkValues) {
        return children;
    }
    // Has to wait, but don't want to
    if (waitForInitialization !== true && waitForCache !== true) {
        return children;
    }
    // Wait until cache is ready
    if (waitForCache && hasCacheValues) {
        return children;
    }
    // Wait until initialized from network
    if (waitForInitialization && hasNetworkValues) {
        return children;
    }
    // Wait until initialized and I have a custom loading component
    if ((waitForInitialization || waitForCache) &&
        initializingComponent != null) {
        return initializingComponent;
    }
    // Cannot render yet
    return null;
}
